import { GrowthBook } from '@growthbook/growthbook-react'
import { API, Cache } from 'aws-amplify'
import gtag from 'utils/gtag'
import { getCognitoId, getVisitorId } from './UserId'

const devMode = process.env.GATSBY_USER_BRANCH !== 'production' || process.env.NODE_ENV === 'development'

const sendExperimentEvent = async (result: any) => {
  let visitorId = Cache.getItem('visitorId')
  if (
    [
      '638c084f1f16cf85aed66cf027472004',
      'baefc2443c952ce9109898d9016853d4',
      '696ab977b1e177303a0c0023240ddfdf',
      '814e1afd9f329f4c5aa6e32f9ee9568a',
      '1795d46ba0721e30c39ec35f47e981dd'
    ].includes(visitorId)
  )
    visitorId = ''
  let customerId = await getCognitoId()
  if (!visitorId && !customerId) {
    visitorId = getVisitorId()
  }

  const userId = customerId ? customerId : visitorId

  // send event
  if (result && userId) {
    await API.post('wwwREST', '/v1/public/event', {
      body: {
        experiment: result.featureId,
        variation: result.variationId,
        visitorId: userId,
        name: 'experiment'
      }
    }).catch((err: any) => {
      console.error(JSON.stringify(err, null, 2))
    })
  }
}

export const gb = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: process.env.GATSBY_GROWTHBOOK_KEY || '',
  enableDevMode: devMode,
  // https://docs.growthbook.io/guide/GA4-google-analytics#tracking-experiment-exposure
  trackingCallback: (experiment, result) => {
    console.log(result, 'growthbook result')

    sendExperimentEvent(result)
    growthbookQueueUtil(experiment.key, result.variationId)

    if (typeof window.clarity !== 'undefined') {
      window.clarity('set', experiment.key, result.variationId)
    }
  }
})

export const loadGrowthbook = async (instance: any) => {
  let visitorId = Cache.getItem('visitorId')
  if (
    [
      '638c084f1f16cf85aed66cf027472004',
      'baefc2443c952ce9109898d9016853d4',
      '696ab977b1e177303a0c0023240ddfdf',
      '814e1afd9f329f4c5aa6e32f9ee9568a',
      '1795d46ba0721e30c39ec35f47e981dd'
    ].includes(visitorId)
  )
    visitorId = ''
  if (!visitorId) {
    visitorId = await getVisitorId()
  }
  if (process.env.GATSBY_GROWTHBOOK_KEY && visitorId) {
    instance.setAttributes({
      id: visitorId
    })
    await instance.loadFeatures({ autoRefresh: true })
  }
}

export const growthbookQueueUtil = async (experiment: string, result: number) => {
  const experimentQueue: any = []
  let runQueue = false
  let cookieConsent = false
  if (typeof window.CookieConsent !== 'undefined') {
    cookieConsent = window?.CookieConsent?.consent?.preferences
  }
  if (cookieConsent) {
    gtag('event', 'viewed_experiment', {
      event_category: 'experiment',
      experiment_id: experiment,
      variation_id: result
    })
  } else {
    experimentQueue.push({ experiment, result })
    runQueue = true

    // A loop that attempts every 5 seconds
    const sendGtagsOnConsent = setInterval(() => {
      if (typeof window.CookieConsent !== 'undefined') {
        cookieConsent = window?.CookieConsent?.consent?.preferences
      }
      if (experimentQueue.length > 0 && runQueue && cookieConsent) {
        experimentQueue.forEach((e: any) => {
          gtag('event', 'viewed_experiment', {
            event_category: 'experiment',
            experiment_id: e.experiment,
            variation_id: e.result
          })
        })
        runQueue = false
        clearInterval(sendGtagsOnConsent)
      }
    }, 5000)
  }
}

/**
 * Utility function for GrowthBook A/B testing through Builder
 *
 * @param growthbookFeatureId - Unique identifier for the GrowthBook feature
 * @param weights - Array of weights for each variant
 * @param enabled - Boolean flag to enable/disable the feature
 * @param fallbackValue - Default value to return if feature is disabled or ID is not provided
 * @returns The selected variant ID or fallback value
 *
 */
export const growthbookBuilderVariantUtil = (
  growthbookFeatureId: string,
  weights: any,
  enabled: boolean,
  fallbackValue: any
) => {
  if (enabled) {
    let variantId = null
    const key = `builderAbComponent_${growthbookFeatureId}`
    variantId = Cache.getItem(key)
    if (typeof variantId === 'number') return variantId
    if (growthbookFeatureId && typeof variantId !== 'number') {
      const getRandomIndexWithWeights = weights => {
        const totalWeight = weights.reduce((sum, w) => sum + w, 0)
        let randomValue = Math.random() * totalWeight
        let index = 0
        while (randomValue > 0) {
          randomValue -= weights[index]
          index++
        }
        return index
      }
      if (enabled) {
        variantId = getRandomIndexWithWeights(weights)
        growthbookQueueUtil(growthbookFeatureId, variantId)
        Cache.setItem(key, variantId, { expires: Date.now() + 90 * 24 * 60 * 60 * 1000 }) // 90 days
      }
      console.error(`GB feature Id - ${growthbookFeatureId}`, `GB variant - ${variantId}`)
      return variantId
    }
  }
  if (!enabled || !growthbookFeatureId) return fallbackValue
}

// Growthbook experiments
